import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ArchitectureGraphSettings } from '../useArchitectureGraphSettings';

export interface ArchitectureGraphSettingsModalProps {
  initialSettings: ArchitectureGraphSettings;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (settings: ArchitectureGraphSettings) => Promise<void> | void;
}

export function ArchitectureGraphSettingsModal({
  initialSettings,
  saving,
  error,
  onClose,
  onSave,
}: ArchitectureGraphSettingsModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [draft, setDraft] = useState(initialSettings);
  const dirty = draft.zoomOnClick !== initialSettings.zoomOnClick;

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !saving) onClose();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, saving]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/45 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="architecture-graph-settings-title"
        className="w-full max-w-md rounded-xl bg-white shadow-2xl"
        onSubmit={(event) => {
          event.preventDefault();
          if (!dirty || saving) return;
          void onSave(draft);
        }}
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-6 py-4">
          <div>
            <h2
              id="architecture-graph-settings-title"
              className="font-inter-tight text-xl font-semibold text-neutral-900"
            >
              Graph settings
            </h2>
            <p className="mt-1 text-body text-neutral-600">
              Control how the architecture graph responds to selections.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900 disabled:cursor-not-allowed disabled:text-neutral-300"
            aria-label="Close graph settings"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
        <div className="space-y-4 px-6 py-5">
          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-small text-red-700">
              {error}
            </p>
          ) : null}
          <div className="flex items-center justify-between gap-4 rounded-lg border border-neutral-200 px-4 py-3">
            <div>
              <p className="text-body font-semibold text-neutral-900">Zoom on click</p>
              <p className="mt-1 text-small text-neutral-600">
                Focus selected nodes and the endpoints of selected connections.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={draft.zoomOnClick}
              aria-label="Zoom on click"
              disabled={saving}
              onClick={() =>
                setDraft((current) => ({ ...current, zoomOnClick: !current.zoomOnClick }))
              }
              className={cn(
                'focus-ring relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition disabled:cursor-not-allowed disabled:opacity-60',
                draft.zoomOnClick
                  ? 'border-backoffice-500 bg-backoffice-500'
                  : 'border-neutral-300 bg-neutral-200'
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'block h-5 w-5 rounded-full bg-white shadow-sm transition',
                  draft.zoomOnClick ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-neutral-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-neutral-200 px-4 py-2 text-small font-semibold text-neutral-700 hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:text-neutral-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !dirty}
            className="focus-ring inline-flex items-center rounded-lg bg-backoffice-500 px-4 py-2 text-small font-semibold text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:bg-backoffice-300"
          >
            {saving ? (
              <span className="inline-flex items-center whitespace-nowrap">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                &nbsp;Save settings
              </span>
            ) : (
              'Save settings'
            )}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
